import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Input, Modal } from "antd"
import { useTranslation } from "react-i18next"
import { buildBlackoutReservationsLink, openReservationsLink } from "helpers/blackouts"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import { Text } from "ui/Typography"
import BlackoutTargetSelector from "../../components/BlackoutTargetSelector"
import AppliesOnRows from "./AppliesOnRows"
import AffectsPanel from "./AffectsPanel"
import { useBlackoutForm } from "./useBlackoutForm"
import { BLACKOUT_SCOPES, SCOPE_COPY, SCOPES_WITH_ALL_OPTION } from "../../constants"
import styles from "./index.module.css"

/**
 * One modal serves all blackout scopes. The "Blackout Settings" and "Affects" cards are
 * identical everywhere; only the target picker inside "Affects" varies, and business
 * blackouts have no picker at all because they apply to everything.
 */
const BlackoutFormModal = ({ isOpen, scope, blackout, onCancel, onSubmit, isSaving }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()

  const isBusinessScope = scope === BLACKOUT_SCOPES.BUSINESS
  const copy = SCOPE_COPY[scope]

  const {
    reason,
    setReason,
    entries,
    setEntries,
    isRecurringYearly,
    setIsRecurringYearly,
    selectedIds,
    setSelectedIds,
    targetOptions,
    isLoadingTargets,
    impact,
    isLoadingImpact,
    isValid,
    isDirty,
    entryErrors,
    buildPayload
  } = useBlackoutForm({ scope, blackout, isOpen })

  const [isUnsavedPromptOpen, setIsUnsavedPromptOpen] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  /**
   * Bookings already on the calendar that this blackout would land on. They are what makes
   * applying consequential: the blackout takes effect immediately, but cancelling or moving each
   * booking stays the guide's decision — so above zero, Apply asks first.
   */
  const conflictingCount = impact?.conflicting_bookings_count || 0

  const submit = () => {
    setIsConfirmOpen(false)
    // The impact travels with the payload so the page can report what still needs handling
    // without re-deriving it from a preview that is cleared as the modal closes.
    onSubmit(buildPayload(), impact)
  }

  const handleApply = () => (conflictingCount > 0 ? setIsConfirmOpen(true) : submit())

  /**
   * The bookings this blackout would break, on the reservations list — narrowed to the dates
   * currently in the form and the listings the preview says it hits, so the count in the panel
   * and the list behind the link describe the same thing.
   *
   * It opens in a new tab rather than navigating: the guide is mid-edit, and a route change
   * unmounts the modal outright, dropping the form without even the unsaved-changes prompt.
   */
  const openConflictingBookings = () => openReservationsLink(buildBlackoutReservationsLink({ scope, entries, impact }))

  // `Text` writes font weight as an inline style, so the sheet's lighter block heading has to be
  // a prop rather than a CSS override.
  const blockTitleWeight = isMobile ? "500" : "600"

  /**
   * Every way out of the form routes through here — the Cancel button, the ✕, the mask and
   * Escape all reach antd's `onCancel` — so an edited form can never be discarded silently.
   */
  const requestCancel = () => {
    if (isDirty) {
      setIsUnsavedPromptOpen(true)
      return
    }
    onCancel()
  }

  const discardAndClose = () => {
    setIsUnsavedPromptOpen(false)
    onCancel()
  }

  const title = blackout
    ? t("settings.blackout.edit_title", { defaultValue: "Edit Blackout" })
    : t(copy.titleKey, { defaultValue: copy.titleDefault })

  const content = (
    <div className={styles.content}>
      <section className={styles.card}>
        <Text variant="sub" size="lg" fw={blockTitleWeight} className={styles.cardTitle}>
          {t("settings.blackout.settings_section", { defaultValue: "Blackout Settings" })}
        </Text>

        <label className={styles.field}>
          <Text variant="sub" size="md" fw="500" className={styles.fieldLabel}>
            {t("settings.blackout.reason", { defaultValue: "Reason/Note" })}
            <span className={styles.optional}>{t("settings.blackout.optional", { defaultValue: "(Optional)" })}</span>
          </Text>
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t("settings.blackout.reason_placeholder", { defaultValue: "e.g. Weather closures" })}
          />
        </label>

        <AppliesOnRows
          entries={entries}
          onChange={setEntries}
          isRecurringYearly={isRecurringYearly}
          onRecurringChange={setIsRecurringYearly}
          entryErrors={entryErrors}
        />
      </section>

      <section className={styles.card}>
        <Text variant="sub" size="lg" fw={blockTitleWeight} className={styles.cardTitle}>
          {t("settings.blackout.affects_section", { defaultValue: "Affects" })}
        </Text>

        {!isBusinessScope && (
          <div className={styles.field}>
            <Text variant="sub" size="md" fw="500" className={styles.fieldLabel}>
              {t(copy.targetLabelKey, { defaultValue: copy.targetLabelDefault })}
              <span className={styles.required}>*</span>
            </Text>
            <BlackoutTargetSelector
              options={targetOptions}
              value={selectedIds}
              onChange={setSelectedIds}
              placeholder={t("settings.blackout.select_placeholder", { defaultValue: "Select" })}
              showAllOption={SCOPES_WITH_ALL_OPTION.includes(scope)}
              isLoading={isLoadingTargets}
            />
          </div>
        )}

        <AffectsPanel
          scope={scope}
          impact={impact}
          isLoading={isLoadingImpact}
          hasTargets={selectedIds.length > 0}
          onViewBookings={openConflictingBookings}
        />
      </section>

    </div>
  )

  /**
   * The two actions. On the phone sheet they are pinned below the scrolling body by the drawer's
   * own footer slot and split the width evenly; in the desktop modal they stay inline and
   * right-aligned, which is where the rest of Settings puts them.
   */
  const actions = (
    <Flex justify="flex-end" gap="var(--spacing-s)" className={isMobile ? styles.sheetActions : styles.footer}>
      <Button shape="round" onClick={requestCancel} disabled={isSaving} className={isMobile ? styles.sheetButton : undefined}>
        {t("settings.blackout.cancel", { defaultValue: "Cancel" })}
      </Button>
      <Button
        color="primary"
        variant="solid"
        shape="round"
        onClick={handleApply}
        // Applying mid-preview would commit a blackout whose stated impact is still the old one.
        disabled={!isValid || isSaving || isLoadingImpact}
        loading={isSaving}
        className={isMobile ? styles.sheetButton : undefined}
      >
        {t("settings.blackout.apply", { defaultValue: "Apply Blackout" })}
      </Button>
    </Flex>
  )

  /* Figma 6333:112295 — no icon, and the confirm is destructive-red because it commits over
     bookings that are already sold. */
  const applyPrompt = (
    <NewConfirmModal
      visible={isConfirmOpen}
      hasIcon={false}
      onClose={() => setIsConfirmOpen(false)}
      onConfirm={submit}
      title={t("settings.blackout.confirm_apply_title", { defaultValue: "Apply Blackout?" })}
      message={t("settings.blackout.confirm_apply_message", { defaultValue: "This blackout will affect existing bookings." })}
      warningText={
        conflictingCount === 1
          ? t("settings.blackout.confirm_apply_bookings_single", {
              defaultValue: "1 existing booking will be impacted and may require cancellation or rescheduling."
            })
          : t("settings.blackout.confirm_apply_bookings", {
              defaultValue: "{{count}} existing bookings will be impacted and may require cancellation or rescheduling.",
              count: conflictingCount
            })
      }
      confirmLabel={t("settings.blackout.apply", { defaultValue: "Apply Blackout" })}
      cancelLabel={t("settings.blackout.cancel", { defaultValue: "Cancel" })}
      loading={isSaving}
    />
  )

  const unsavedPrompt = (
    <UnsavedChangesModal
      open={isUnsavedPromptOpen}
      onDiscard={discardAndClose}
      onKeepEditing={() => setIsUnsavedPromptOpen(false)}
      zIndex={1200}
    />
  )

  if (isMobile) {
    return (
      <>
        <CustomDrawer hasHandle title={title} open={isOpen} setOpen={requestCancel} footer={actions} className={styles.sheet}>
          {content}
        </CustomDrawer>
        {applyPrompt}
        {unsavedPrompt}
      </>
    )
  }

  return (
    <Modal open={isOpen} onCancel={requestCancel} title={title} width={800} centered footer={null} className={styles.modal}>
      {content}
      {actions}
      {applyPrompt}
      {unsavedPrompt}
    </Modal>
  )
}

BlackoutFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  scope: PropTypes.oneOf(Object.values(BLACKOUT_SCOPES)).isRequired,
  blackout: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool
}

export default BlackoutFormModal
